import { DateTime } from 'luxon';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { tap } from 'rxjs';

import { ChangeDetectionStrategy, Component, Optional, ViewEncapsulation } from '@angular/core';
import { FormControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AccountEntity, ActivityEntityName, Allocation, AllocationBookingStatus, AllocationStatus, AllocationType, BookingStatus, ContactType, DateUtility, Source } from '@jms/model';
import { ConfirmService } from '@jms/ui/ui.confirm';
import { PickerFetch } from '@jms/ui/ui.core.pickers';
import { JmsValidators } from '@jms/utils/util.validators';

import { AppointmentEditorContext } from '../../../common';
import { SelectDateTimeRange } from '../../model';
import { AllocationUtility } from '@jms/shared/module.scheduling.allocations.pro/src/helpers';
import { TimesConstraintUtil } from '@jms/utils/util.times.constraint';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'appointment-editor',
  templateUrl: './appointment.editor.component.html',
  styleUrls: ['./appointment.editor.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AppointmentEditorComponent {

  public context: AppointmentEditorContext;
  public resources: AccountEntity[] = [];

  public form: FormGroup;
  public dateControl: FormControl = new FormControl();

  public readonly Source = Source;
  public readonly ContactType = ContactType;
  public readonly PickerFetch = PickerFetch;
  public readonly bookingStatusItems = AllocationBookingStatus;

  public readonly typeOptions = [
    AllocationType.PhoneCall,
    AllocationType.Meeting,
    AllocationType.Onsite,
  ];

  public readonly statusOptions = [
    AllocationStatus.Open,
    AllocationStatus.Completed
  ]

  public get isOpportunityEntity(): boolean {
    return this.context?.entity === ActivityEntityName.Opportunity;
  }

  public get isEdit(): boolean {
    return !!this.context?.appointment?._id;
  }

  public get isReadOnly(): boolean {
    return this.context?.appointment?.status === AllocationStatus.Completed;
  }

  public get canSave(): boolean {
    const isRun = AllocationUtility.isShadow(this.context?.appointment)
    const invalidStatus = [AllocationStatus.Cancelled, AllocationStatus.Completed].includes(this.context?.appointment?.status);
    const locked = this.context?.appointment?.locked;

    return [isRun, invalidStatus, locked].every(x => !x);
  }


  constructor(
    private fb: FormBuilder,
    private confirmService: ConfirmService,
    @Optional() public dialog: DynamicDialogRef,
    @Optional() private config: DynamicDialogConfig,
  ) {

    this.init();

    if (!this.context) return;

    const date = this.getAppointmentDate(this.context.appointment, this.context.timezone);

    this.dateControl.setValue(date, { emitEvent: false });

    this.dateControl.valueChanges
      .pipe(
        takeUntilDestroyed(),
        tap(x => {
          const { startDate, endDate } = this.calculateStartEndDate(x);
          this.form.patchValue({ startDate: startDate, endDate: endDate });
        })
      )
      .subscribe();


    TimesConstraintUtil.timesConstraint(
      this.form.get('startDate') as FormControl,
      this.form.get('endDate') as FormControl,
      { timezone: this.context.timezone }
    ).pipe(
      takeUntilDestroyed(),
    ).subscribe();

  }

  private calculateStartEndDate(originDate: DateTime | Date | string): { startDate: Date, endDate: Date } {
    const startDate = this.form.get('startDate').value;
    const endDate = this.form.get('endDate').value;

    return TimesConstraintUtil.calculateStartEndDate(originDate, startDate, endDate, this.context.timezone);
  }

  onSelectedDateTimeRangeChanged(event: SelectDateTimeRange): void {

    const date = DateUtility.getDateTime(event.start || event.end, this.context.timezone).startOf('day').toJSDate();
    this.dateControl.setValue(date, { emitEvent: false });

    const resource = this.context.source === Source.ContractorPortal
      ? { contractorResource: event.resource }
      : { resource: event.resource }

    this.form.patchValue({
      startDate: event.start.toJSDate(),
      endDate: event.end.toJSDate(),
      ...resource
    });

    this.dateControl.patchValue(event.start.toJSDate());

  }

  public onClose(): void {
    if (this.form.dirty) {
      this.confirmService.confirm('Unsaved Changes', 'You have unsaved changes. Are you sure you wish to continue?', 'warning')
        .then(confirm => {

          if (confirm) {
            this.dialog.close();
            return;
          }

        });
    } else {
      this.dialog.close();
    }
  }

  public onSave(): void {
    const value = this.form.value as Allocation;
    const { startDate, endDate } = this.calculateStartEndDate(this.dateControl.value);

    value.locked = !!value.status && value.status === AllocationStatus.Completed;
    value.startDate = startDate;
    value.endDate = endDate;

    // Additional Days is a text input so non-numeric entries fail the numeric validator and keep Save disabled;
    // convert the validated value back to a number (undefined when blank) to honour the numeric API contract.
    if (value.additionalDays != null) {
      const additionalDays = `${value.additionalDays}`.trim();
      value.additionalDays = additionalDays === '' ? undefined : Number(additionalDays);
    }

    this.dialog.close(value);
  }

  private buildForm(context: AppointmentEditorContext): FormGroup {

    const { source, timezone, entity, appointment } = context;

    const { startDate, endDate } = this.getDefaultTime(context);

    const form: FormGroup = this.fb.group({
      _id: [null, []],
      startDate: new FormControl(startDate, { nonNullable: true, validators: [Validators.required] }),
      endDate: new FormControl(endDate, { nonNullable: true, validators: [Validators.required] }),
      note: [null, []],
      resource: [[], [Validators.required]],
      contractorResource: [null],
      source: new FormControl(source, { nonNullable: true }),
      status: new FormControl(AllocationStatus.Open, { nonNullable: true }),
      bookingStatus: new FormControl(BookingStatus.Confirmed, { nonNullable: true }),
      additionalDays: [null, [Validators.min(0), Validators.max(10), Validators.pattern(JmsValidators.getNumberPattern())]],
      timezone: new FormControl(timezone, { nonNullable: true }),
    });

    if (entity === ActivityEntityName.Opportunity) {
      form.addControl('name', new FormControl(null));
      form.addControl('type', new FormControl(null,));
    }

    if (appointment?._id) {
      form.removeControl('additionalDays');
      form.patchValue(appointment);
    }

    if (source === Source.ContractorPortal) {
      form.get('contractorResource').setValidators([Validators.required]);
      form.patchValue({ resource: context.contractor });
    }

    return form;

  }

  private init(): void {
    const context: AppointmentEditorContext = this.config?.data?.context;
    if (!context) return;

    this.context = context;
    this.form = this.buildForm(context);

    const date = this.getAppointmentDate(context.appointment, context.timezone);
    this.dateControl.setValue(date, { emitEvent: false });

    if (this.isReadOnly) {
      this.form.disable({ emitEvent: false });
      this.dateControl.disable({ emitEvent: false });
    }
  }

  private getDefaultTime(context: AppointmentEditorContext): { startDate: Date, endDate: Date } {

    const { settings, timezone } = context;

    const [startHour, startMinute] = (settings?.schedule?.dayStartTime ?? '9:00')
      .split(':')
      .map(x => parseInt(x.trim()));

    const date = DateTime.local().setZone(timezone).startOf('day');

    const startDate = date.set({ hour: startHour, minute: startMinute });
    const endDate = startDate.plus({ hour: 1 });

    return {
      startDate: startDate.toJSDate(),
      endDate: endDate.toJSDate()
    };

  }

  private getAppointmentDate(appointment: Allocation, timezone: string): DateTime {

    const date = !appointment
      ? DateTime.local().setZone(timezone)
      : DateUtility.getDateTime(appointment.startDate, timezone);

    return date.startOf('day');

  }
}
